import { useEffect, useState, useCallback } from 'react';
import { Stream } from "../../../Models/Stream";
import { useParams } from 'react-router-dom';
import {
  Typography,
  Button,
  CircularProgress,
  Box,
  TextField,
  Stack,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Moment from 'moment';
import { StreamCompleted } from '../../../Models/StreamCompleted';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import { useUserContext } from '../../../Context/UserContext';
import { useAPIContext } from '../../../Context/APIContext';

export default function ViewStream() {
  const [source, setSource] = useState<string>("");
  const [stream, setStream] = useState<Stream>();
  const [loading, setLoading] = useState<boolean>(false);
  const [completedStream, setCompletedStream] = useState<StreamCompleted>();
  const [userResponse, setUserResponse] = useState<string>("");
  const { streamId } = useParams();
  const [awaitingResponse, setAwaitingResponse] = useState<boolean>(false);
  const { fetchPoints } = useUserContext();
  const { apiFetch } = useAPIContext();

  const MIN_CHARS = 200;
  const draftKey = `stream-draft-${streamId}`;
  const charCount = userResponse.length;
  const hasText = charCount > 0;
  const isTooShort = hasText && charCount < MIN_CHARS;

  const fetchStreamData = useCallback(async () => {
    setLoading(true);

    if (streamId) {
      const streamData = await apiFetch<Stream>(`Streams/${streamId}`, 'GET', Stream.fromJson);

      if (streamData) {
        setStream(streamData);
        setSource(`https://streams.bitmovin.com/${streamData.bitmovinId}/embed?token=${streamData.accessToken}`);
      }

      const streamCompletedData = await apiFetch<StreamCompleted>(`Users/CompletedStreams/${streamId}`, 'GET', StreamCompleted.fromJson);

      if (streamCompletedData) {
        setCompletedStream(streamCompletedData);
        setUserResponse(streamCompletedData.submissionText || "");
      }
    }

    setLoading(false);
  }, [streamId, apiFetch]);


  const handleMarkAsComplete = async () => {
    setAwaitingResponse(true);

    const completion = new StreamCompleted(
      0,
      stream?.id || 0,
      "",
      0,
      new Date(),
      stream?.points || 0,
      userResponse
    );

    const response = await apiFetch<number>('Users/CompletedStreams', 'POST', completion);
    if (response && response > 0) {
      completion.id = response;
      setCompletedStream(completion);
      fetchPoints();

      // Clear draft
      localStorage.removeItem(draftKey);
    }

    setAwaitingResponse(false);
  };

  // Fetch stream + completed data on mount
  useEffect(() => {
    fetchStreamData();
  }, [fetchStreamData]);

  // Load draft from localStorage if not completed
  useEffect(() => {
    if (!completedStream?.id && streamId) {
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        setUserResponse(savedDraft);
      }
    }
  }, [streamId, completedStream?.id, draftKey]);

  // Autosave draft to localStorage
  useEffect(() => {
    if (!completedStream?.id && streamId) {
      localStorage.setItem(draftKey, userResponse);
    }
  }, [userResponse, completedStream?.id, streamId, draftKey]);

  return (
    <Box sx={{
      maxWidth: '700px',
      pl: { xs: 1, sm: 4 },
      pr: { xs: 1, sm: 2 },
      pt: 0,
      pb: 4
    }}>
      <Box sx={{ p: { xs: 2, sm: 1 } }}>
        {loading ? (
          <Box display="flex" justifyContent="flex-start" p={4}>
            <CircularProgress style={{ color: '#3D9BE9' }} />
          </Box>
        ) : (
          <Stack spacing={3} alignItems="flex-start">
            {/* Title and metadata */}
            <Box width="100%">
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                {stream?.title}
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Box display="flex" alignItems="center">
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary" ml={0.5}>
                    {Moment(stream?.dateAdded).format('DD-MMM-YYYY')}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#f0f4f0',
                    borderRadius: '5px',
                    padding: '4px 6px',
                    color: '#66bb6a',
                    fontWeight: 'bold',
                  }}
                >
                  <ElectricBoltIcon fontSize="small" sx={{ color: '#66bb6a' }} />
                  <Typography variant="body2" fontWeight={600} ml={0.5} color="#66bb6a">
                    {stream?.points} points
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Video player */}
            <Box sx={{
              position: 'relative',
              width: '100%',
              height: 0,
              paddingTop: '56.25%',
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: '#000'
            }}>
              <iframe
                src={source}
                title="Stream Video"
                frameBorder="0"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            </Box>

            <Box width="100%">
              <Typography variant="body1" mb={1}>
                Reflect on how this topic will develop your knowledge and skills:
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Suggested OPAL structure: <strong>Overview</strong> · <strong>Points</strong> · <strong>Actions</strong> · <strong>Learning / Links</strong>
              </Typography>

              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={10}
                variant="outlined"
                value={userResponse}
                onChange={(e) => setUserResponse(e.target.value)}
                label="Your reflection"
                disabled={!!completedStream?.id}
                error={isTooShort}
                helperText={
                  hasText
                    ? `${charCount} / ${MIN_CHARS} characters`
                    : `Minimum ${MIN_CHARS} characters`
                }
                sx={{
                  mb: 2,
                  '& .MuiInputBase-root': {
                    lineHeight: 1.6,
                    backgroundColor: '#fafafa',
                  },
                }}
              />

              {completedStream?.id ? (
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  sx={{
                    backgroundColor: '#66bb6a',
                    fontWeight: 'bold',
                    borderRadius: '5px',
                    py: 1
                  }}
                >
                  Completed
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  disabled={charCount < MIN_CHARS || awaitingResponse}
                  onClick={handleMarkAsComplete}
                  sx={{
                    fontWeight: 600,
                    py: 1,
                    color: '#FFF'
                  }}
                >
                  {awaitingResponse ? "Submitting..." : "Mark as complete"}
                </Button>
              )}
            </Box>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
